/**
 * ThemedModal Component
 * 
 * Modal dialog that adapts its appearance based on the active immersive theme.
 * Supports all 5 themes with unique visual characteristics.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useImmersiveTheme } from '../../hooks/useImmersiveTheme';
import { ImmersiveThemeType, ThemedModalProps } from '../../types/immersive-theme.types';
import {
  neumorphismModalOverlay,
  neumorphismModal,
  neumorphismModalHeader,
} from './theme-styles/neumorphism';
import {
  brutalistModalOverlay,
  brutalistModal,
  brutalistModalHeader,
  brutalistModalCloseButton,
} from './theme-styles/brutalist';
import {
  retroTerminalModalOverlay,
  retroTerminalModal,
  retroTerminalModalHeader,
  retroTerminalModalTitle,
  retroTerminalModalTitlePrefix,
} from './theme-styles/retro-terminal';
import {
  organicNatureModalOverlay,
  organicNatureModal,
  organicNatureModalHeader,
  organicNatureModalTitle,
} from './theme-styles/organic-nature';
import {
  luxuryGoldModalOverlay,
  luxuryGoldModal,
  luxuryGoldModalHeader,
  luxuryGoldModalTitle,
} from './theme-styles/luxury-gold';

// Overlay styles per theme
const overlayStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismModalOverlay,
  [ImmersiveThemeType.BRUTALIST]: brutalistModalOverlay,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalModalOverlay,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureModalOverlay,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldModalOverlay,
};

// Modal container styles per theme
const modalStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    background: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    padding: '24px',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismModal,
  [ImmersiveThemeType.BRUTALIST]: brutalistModal,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalModal,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureModal,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldModal,
};

// Header styles per theme
const headerStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: '16px',
    marginBottom: '20px',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismModalHeader,
  [ImmersiveThemeType.BRUTALIST]: brutalistModalHeader,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalModalHeader,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureModalHeader,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldModalHeader,
};

// Title styles per theme
const titleStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    fontSize: '1.25rem',
    fontWeight: 600,
    margin: 0,
  },
  [ImmersiveThemeType.NEUMORPHISM]: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: 'var(--neu-text)',
    margin: 0,
  },
  [ImmersiveThemeType.BRUTALIST]: {
    fontSize: '1.25rem',
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: '-0.02em',
    margin: 0,
  },
  [ImmersiveThemeType.RETRO_TERMINAL]: {
    ...retroTerminalModalTitle,
    margin: 0,
  },
  [ImmersiveThemeType.ORGANIC_NATURE]: {
    ...organicNatureModalTitle,
    margin: 0,
  },
  [ImmersiveThemeType.LUXURY_GOLD]: {
    ...luxuryGoldModalTitle,
    margin: 0,
  },
};

// Size configurations
const sizeConfig: Record<string, { width: string; maxWidth: string }> = {
  sm: { width: '400px', maxWidth: '90vw' },
  md: { width: '500px', maxWidth: '90vw' },
  lg: { width: '700px', maxWidth: '90vw' },
  xl: { width: '900px', maxWidth: '90vw' },
  full: { width: '95vw', maxWidth: '95vw' },
};

// Close button component
const CloseButton: React.FC<{ onClick: () => void; theme: ImmersiveThemeType }> = ({ onClick, theme }) => {
  const buttonStyles: React.CSSProperties = {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme === ImmersiveThemeType.BRUTALIST ? '0' : '8px',
    transition: 'all 0.15s ease',
    ...(theme === ImmersiveThemeType.BRUTALIST ? brutalistModalCloseButton : {}),
  };

  const iconColor = theme === ImmersiveThemeType.RETRO_TERMINAL 
    ? 'var(--term-text)' 
    : theme === ImmersiveThemeType.LUXURY_GOLD
      ? 'var(--lux-text-secondary)'
      : 'currentColor';

  return (
    <button
      onClick={onClick}
      style={buttonStyles}
      aria-label="Fechar modal"
      type="button"
    >
      {theme === ImmersiveThemeType.BRUTALIST ? (
        <span style={{ fontWeight: 900, fontSize: '18px' }}>×</span>
      ) : (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M5 5L15 15M15 5L5 15"
            stroke={iconColor}
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
};

export const ThemedModal: React.FC<ThemedModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  footer,
  centered = true,
  className,
  children,
}) => {
  const { theme, reducedMotion } = useImmersiveTheme();
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Handle escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && closeOnEscape) {
      onClose();
    }
  }, [closeOnEscape, onClose]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Focus management
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      modalRef.current?.focus();
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      previousActiveElement.current?.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // Don't render if not open
  if (!isOpen) return null;

  // Compute overlay styles
  const computedOverlayStyles: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: centered ? 'center' : 'flex-start',
    justifyContent: 'center',
    padding: centered ? '20px' : '60px 20px 20px',
    zIndex: 1000,
    ...overlayStyles[theme],
    ...(reducedMotion ? { transition: 'none' } : { transition: 'opacity 0.2s ease' }),
  };

  // Compute modal styles
  const computedModalStyles: React.CSSProperties = {
    position: 'relative',
    ...modalStyles[theme],
    ...sizeConfig[size],
    maxHeight: '90vh',
    overflowY: 'auto',
    ...(reducedMotion ? { transition: 'none' } : { 
      animation: 'modalEnter 0.2s ease-out',
    }),
  };

  // Footer styles
  const footerStyles: React.CSSProperties = {
    borderTop: theme === ImmersiveThemeType.BRUTALIST 
      ? 'var(--brut-border)' 
      : theme === ImmersiveThemeType.RETRO_TERMINAL
        ? 'var(--term-border-dim)'
        : theme === ImmersiveThemeType.LUXURY_GOLD
          ? 'var(--lux-border)'
          : '1px solid rgba(0, 0, 0, 0.1)',
    paddingTop: '16px',
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  };

  // Render title with theme-specific styling
  const renderTitle = () => {
    if (!title) return null;

    if (theme === ImmersiveThemeType.RETRO_TERMINAL) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={retroTerminalModalTitlePrefix}>[</span>
          <h2 style={titleStyles[theme]}>{title.toUpperCase()}</h2>
          <span style={retroTerminalModalTitlePrefix}>]</span>
        </div>
      );
    }

    return <h2 style={titleStyles[theme]}>{title}</h2>;
  };

  // Subtitle styles
  const subtitleStyles: React.CSSProperties = {
    fontSize: '0.875rem',
    marginTop: '4px',
    color: theme === ImmersiveThemeType.RETRO_TERMINAL 
      ? 'var(--term-text-dim)' 
      : theme === ImmersiveThemeType.LUXURY_GOLD
        ? 'var(--lux-text-secondary)'
        : 'var(--color-text-secondary, #6b7280)',
    ...(theme === ImmersiveThemeType.RETRO_TERMINAL ? { fontFamily: 'var(--term-font)' } : {}),
  };

  const modalContent = (
    <div
      style={computedOverlayStyles}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <style>{`
        @keyframes modalEnter {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
      
      <div
        ref={modalRef}
        style={computedModalStyles}
        className={className}
        tabIndex={-1}
        role="document"
      >
        {showCloseButton && <CloseButton onClick={onClose} theme={theme} />}
        
        {(title || subtitle) && (
          <div style={headerStyles[theme]}>
            {renderTitle()}
            {subtitle && <p style={subtitleStyles}>{subtitle}</p>}
          </div>
        )}
        
        <div>{children}</div>
        
        {footer && <div style={footerStyles}>{footer}</div>}
      </div>
    </div>
  );

  // Render in portal
  return createPortal(modalContent, document.body);
};

export default ThemedModal;
